import React from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { servicesData } from "@/data/servicesData";
import { Container, PageHeader, QuoteForm, ContactButtons, Picture, FaqList, SERVICES, INKS } from "@/components/brand";

export default function ServicePage() {
  const { slug } = useParams();
  const service = servicesData[slug];

  if (!service) {
    return (
      <div className="min-h-screen bg-paper">
        <Navbar />
        <PageHeader title="We couldn't find that page" lede="The service may have moved. Everything we print is listed below, or call us and we'll point you the right way.">
          <Link to="/#services" className="btn btn-ink">See everything we print</Link>
        </PageHeader>
        <Footer />
      </div>
    );
  }

  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div data-testid="service-page" className="min-h-screen bg-paper">
      <Navbar />
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "What we print", to: "/#services" }, { label: service.title }]}
        title={service.headline}
        lede={service.description}
        aside={service.heroImage ? (
          <Picture src={service.heroImage} alt={service.title} className="hidden lg:block w-full aspect-[4/5] border border-ink shadow-[10px_10px_0_0_#00A3E0]" />
        ) : null}
      >
        <div className="flex flex-wrap gap-3">
          <a href="#quote-form" className="btn btn-magenta">Get a quote</a>
          <ContactButtons message={`Hi, I'd like a quote for ${service.title.toLowerCase()}.`} />
        </div>
      </PageHeader>

      <main>
        <Container className="py-16 md:py-24 grid gap-14 lg:grid-cols-[1fr_minmax(0,25rem)] lg:gap-16">
          <div className="min-w-0 space-y-16 md:space-y-20">
            <section aria-labelledby="about">
              <h2 id="about" className="font-display font-bold text-3xl sm:text-4xl text-ink">
                {service.title} in {service.city === "Bangalore" ? "Bengaluru" : service.city}
              </h2>
              <p className="mt-5 text-lg text-graphite leading-relaxed max-w-[65ch]">{service.longDescription}</p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 border-t border-ink">
                {service.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 py-3.5 border-b border-rule text-ink">
                    <Check size={18} className="mt-1 shrink-0 text-process-magenta" aria-hidden="true" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section aria-labelledby="options">
              <h2 id="options" className="font-display font-bold text-3xl sm:text-4xl text-ink">Options</h2>
              <p className="mt-3 text-graphite">Not sure which? Tell us how you'll use it and we'll recommend one.</p>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {service.variants.map((v, i) => (
                  <li key={v} className="flex items-center gap-3 bg-stock border border-ink px-4 py-4 font-display font-medium text-ink">
                    <span className="w-3 h-3 shrink-0" style={{ backgroundColor: INKS[i % 4] }} aria-hidden="true" />
                    {v}
                  </li>
                ))}
              </ul>
            </section>

            <section aria-labelledby="why" className="bg-ink text-paper p-6 sm:p-10 -mx-4 sm:mx-0">
              <h2 id="why" className="font-display font-bold text-3xl sm:text-4xl">Why order from us</h2>
              <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {service.whyChoose.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-paper/90">
                    <Check size={18} className="mt-1 shrink-0 text-process-yellow" aria-hidden="true" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section aria-labelledby="faq">
              <h2 id="faq" className="font-display font-bold text-3xl sm:text-4xl text-ink mb-8">Questions about {service.title.toLowerCase()}</h2>
              <FaqList faqs={service.faqs} testid="service-faq" />
            </section>
          </div>

          <aside id="quote-form" className="lg:sticky lg:top-24 self-start scroll-mt-24">
            <QuoteForm
              testid="service-quote-form"
              title={`Quote for ${service.title.toLowerCase()}`}
              service={service.title}
              lockService
              messagePlaceholder="Quantity, size, finish, and your deadline"
              submitLabel="Get my quote"
              className="shadow-[8px_8px_0_0_#161616]"
            />
          </aside>
        </Container>

        <section className="border-t border-ink bg-stock py-14 md:py-16">
          <Container>
            <h2 className="font-display font-semibold text-graphite mb-6">We also print</h2>
            <ul className="flex flex-wrap gap-2">
              {others.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`}
                    className="inline-flex items-center gap-1 rounded-full border border-ink px-4 py-2 font-display font-medium text-ink hover:bg-ink hover:text-paper transition-colors">
                    {s.name} <ArrowUpRight size={14} aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
}
